import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CaseStudiesPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = ['All', 'Retail', 'Automotive', 'Manufacturing'];

  const caseStudies = [
    {
      id: 'retail-pos-transformation',
      title: 'Retail POS Transformation',
      client: 'Al-Manar Retail Group',
      category: 'Retail',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop',
      description: 'Unified 12 retail stores under a single Odoo POS system, eliminating inventory discrepancies and reducing checkout times by 65%.',
      results: [
        { metric: '65%', label: 'Faster Checkout' },
        { metric: '99.9%', label: 'System Uptime' },
        { metric: '40%', label: 'Inventory Accuracy' }
      ],
      technologies: ['Odoo 17', 'Odoo POS', 'Odoo Inventory', 'Odoo Accounting'],
      challenge: 'Legacy POS systems across 12 locations with no integration, 25% inventory discrepancies, and 4+ minute checkout times during peak hours.',
      solution: 'Implemented Odoo POS with offline capability, real-time inventory sync, automated reorder rules, and a custom loyalty program.',
      timeline: '4 months'
    },
    {
      id: 'car-rental-fleet-management',
      title: 'Car Rental Fleet Management System',
      client: 'Desert Drive Car Rental',
      category: 'Automotive',
      image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&h=600&fit=crop',
      description: 'Built a complete car rental management ecosystem for 850+ vehicles across 15 locations, eliminating double-bookings and increasing revenue by 30%.',
      results: [
        { metric: '45%', label: 'Fleet Utilization' },
        { metric: '60%', label: 'Faster Booking' },
        { metric: '30%', label: 'Revenue Increase' }
      ],
      technologies: ['Odoo 17', 'Odoo Fleet', 'Odoo Website', 'Odoo CRM', 'Custom Modules'],
      challenge: 'Double-bookings 3-4 times weekly, manual paperwork taking 15+ minutes per rental, no real-time fleet visibility, and maintenance tracked in spreadsheets.',
      solution: 'Developed custom Odoo Rental module with availability calendar, digital contracts, automated maintenance scheduling, and customer booking portal.',
      timeline: '5 months'
    },
    {
      id: 'manufacturing-erp-implementation',
      title: 'Manufacturing ERP Implementation',
      client: 'Precision Steel Industries',
      category: 'Manufacturing',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop',
      description: 'Digitized entire steel fabrication operation with Odoo Manufacturing, achieving 100% material traceability and 35% production efficiency gains.',
      results: [
        { metric: '35%', label: 'Efficiency Gain' },
        { metric: '50%', label: 'Inventory Cost Cut' },
        { metric: '80%', label: 'Faster Processing' }
      ],
      technologies: ['Odoo 17', 'Odoo Manufacturing', 'Odoo MRP', 'Odoo Quality', 'IoT Sensors'],
      challenge: 'Production planning in spreadsheets, 70% inventory accuracy, paper-based quality records, no material traceability, and reactive maintenance causing stoppages.',
      solution: 'Implemented Odoo Manufacturing with shop floor tablets, barcode tracking, quality checkpoints, IoT machine monitoring, and real-time job costing.',
      timeline: '7 months'
    }
  ];

  const filteredStudies = activeFilter === 'All' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter);

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 lg:py-28">
        <div className="absolute inset-0 opacity-10 bg-white/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Case Studies & Success Stories
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Discover how we've helped businesses transform their operations with tailored Odoo implementations. Each project tells a story of measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-1">50+</div>
              <div className="text-gray-600">Odoo Implementations</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-1">98%</div>
              <div className="text-gray-600">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-1">35%</div>
              <div className="text-gray-600">Avg Efficiency Gain</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-1">8</div>
              <div className="text-gray-600">Industries Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50 sticky top-20 z-30 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  activeFilter === filter
                    ? 'bg-gray-900 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8">
            {filteredStudies.map((study) => (
              <Link 
                key={study.id}
                to={`/case-studies/${study.id}`}
                className="block bg-white overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="grid lg:grid-cols-2">
                  <div className="aspect-video lg:aspect-auto overflow-hidden">
                    <img 
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm font-semibold">
                        {study.category}
                      </span>
                      <span className="text-sm text-gray-500">{study.client}</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 group-hover:text-gray-700 transition-colors">{study.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{study.description}</p>
                    
                    {/* Results */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center p-4 bg-gray-50">
                          <div className="text-2xl font-bold text-gray-900 mb-1">{result.metric}</div>
                          <div className="text-xs text-gray-500">{result.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Tech Stack */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {study.technologies.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="inline-flex items-center gap-2 text-gray-900 font-semibold group-hover:gap-3 transition-all">
                      Read Full Case Study
                      <span className="material-symbols-outlined text-xl">arrow_forward</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Odoo Implementation Specialists
            </h2>
            <p className="text-lg text-gray-600">
              We specialize in tailored Odoo implementations that solve real business problems and deliver measurable results.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: 'point_of_sale', 
                title: 'Retail & POS', 
                description: 'Complete retail solutions with Odoo POS, inventory management, loyalty programs, and multi-location synchronization.' 
              },
              { 
                icon: 'directions_car', 
                title: 'Fleet & Rental', 
                description: 'Custom rental management systems with booking portals, fleet tracking, maintenance scheduling, and automated billing.' 
              },
              { 
                icon: 'precision_manufacturing', 
                title: 'Manufacturing', 
                description: 'End-to-end manufacturing ERP with MRP, quality control, shop floor management, and IoT integration.' 
              }
            ].map((service, index) => (
              <div key={index} className="p-8 bg-gray-50 border border-gray-100">
                <div className="w-14 h-14 bg-gray-900 flex items-center justify-center text-white mb-6">
                  <span className="material-symbols-outlined text-2xl">{service.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              How We Deliver Results
            </h2>
            <p className="text-lg text-gray-600">
              Every successful implementation follows our proven methodology, refined over 50+ Odoo projects.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: 'search', title: 'Discovery', description: 'We analyze your current processes, pain points, and goals.' },
              { icon: 'architecture', title: 'Design', description: 'We design the optimal Odoo configuration and customizations.' },
              { icon: 'code', title: 'Implementation', description: 'Agile development with weekly demos and continuous feedback.' },
              { icon: 'rocket_launch', title: 'Go-Live & Support', description: 'Training, deployment, and ongoing optimization.' }
            ].map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="w-16 h-16 bg-gray-900 flex items-center justify-center text-white mx-auto mb-6 shadow-lg">
                  <span className="material-symbols-outlined text-2xl">{step.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how a tailored Odoo implementation can solve your business challenges and drive measurable results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-900 bg-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Get a Free Consultation
              <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border border-white/20 hover:bg-white/10 transition-all duration-200"
            >
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CaseStudiesPage;
